/**
 * Datadog RUM (Real User Monitoring) — boot and error reporting.
 *
 * RUM runs on every page; it is not an error-path concern. Its configuration is
 * built server-side from the DD_JS_* environment variables
 * (config/initializers/002_datadog.rb) and rendered into an inert
 * <script type="application/json"> tag by app/views/platforms/_monitoring.html.erb.
 * The monitoring pack is the only thing that reads it.
 *
 * Booting from the pack rather than from an inline <script> is what makes that
 * work under Shakapacker: the pack's own <script> is emitted by the single
 * javascript_pack_tag in layouts/_head, far below the partial that renders the
 * config, so an inline initialiser runs before the SDK global it needs exists.
 */

type RumConfig = Record<string, unknown>

export interface RumSdk {
  init: (config: RumConfig) => void
  setUser: (user: Record<string, unknown>) => void
  setAccount: (account: Record<string, unknown>) => void
  addError: (error: unknown) => void
}

export interface RumPayload {
  config: RumConfig
  user?: Record<string, unknown>
  account?: Record<string, unknown>
}

export const RUM_CONFIG_ELEMENT_ID = "datadog-rum-config"

export const readRumPayload = (doc: Document = document): RumPayload | null => {
  const element = doc.getElementById(RUM_CONFIG_ELEMENT_ID)

  if (!element || !element.textContent) return null

  return JSON.parse(element.textContent) as RumPayload
}

/**
 * JSON carries no regex literal, so the server sends its tracing-URL patterns as
 * strings under regexAllowedTracingUrls. Compile them onto allowedTracingUrls,
 * which is the key the SDK actually reads. A pattern is either a bare string or
 * an object whose `match` is the pattern (the rest, e.g. propagatorTypes, rides
 * along untouched).
 */
export const buildRumConfig = (config: RumConfig): RumConfig => {
  const { regexAllowedTracingUrls, ...rest } = config
  const allowedTracingUrls = [...((rest.allowedTracingUrls as unknown[]) || [])]

  const patterns = (regexAllowedTracingUrls as unknown[]) || []

  patterns.forEach((pattern) => {
    if (typeof pattern === "object" && pattern !== null && !Array.isArray(pattern)) {
      const entry = pattern as { match: string }
      allowedTracingUrls.push({ ...entry, match: new RegExp(entry.match) })
    } else {
      allowedTracingUrls.push(new RegExp(pattern as string))
    }
  })

  return { ...rest, allowedTracingUrls }
}

/**
 * Initialise RUM from the server-rendered config. Returns false when there is
 * nothing to boot — the config tag is absent in every environment that does not
 * load the datadog gem (development, test), and that is not an error.
 */
export const startRum = (rum: RumSdk, doc: Document = document): boolean => {
  try {
    const payload = readRumPayload(doc)

    if (!payload) return false

    rum.init(buildRumConfig(payload.config))

    if (payload.user && Object.keys(payload.user).length > 0) rum.setUser(payload.user)
    if (payload.account && Object.keys(payload.account).length > 0) rum.setAccount(payload.account)

    return true
  } catch (e) {
    console.warn("Datadog RUM could not be initialized:", e)

    return false
  }
}

/**
 * Report an error to the initialised RUM instance the monitoring pack published.
 *
 * Importing @datadog/browser-rum here would both pull the SDK into the importing
 * entrypoint's bundle and report against a second module instance — each pack is
 * its own webpack entrypoint with its own module registry, and addError on an
 * instance that was never init'd is dropped. window.DD_RUM is the live one.
 */
export const reportError = (error: Error): void => {
  const rum = (window as Window & { DD_RUM?: RumSdk }).DD_RUM

  if (!rum) return

  rum.addError(error)
}
